"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PluggableBarChart = void 0;
var tslib_1 = require("tslib");
// (C) 2019-2022 GoodData Corporation
var react_1 = tslib_1.__importDefault(require("react"));
var isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
var sdk_ui_1 = require("@gooddata/sdk-ui");
var sdk_model_1 = require("@gooddata/sdk-model");
var PluggableColumnBarCharts_1 = require("../PluggableColumnBarCharts");
var supportedProperties_1 = require("../../../constants/supportedProperties");
var BarChartConfigurationPanel_1 = tslib_1.__importDefault(require("../../configurationPanels/BarChartConfigurationPanel"));
var axis_1 = require("../../../constants/axis");
var SortConfig_1 = require("../../../interfaces/SortConfig");
var bucketHelper_1 = require("../../../utils/bucketHelper");
var sortHelpers_1 = require("./sortHelpers");
var sort_1 = require("../../../utils/sort");
/**
 * PluggableBarChart
 *
 * ## Buckets
 *
 * | Name     | Id       | Accepts             |
 * |----------|----------|---------------------|
 * | Measures | measures | measures only       |
 * | ViewBy   | view     | attributes or dates |
 * | StackBy  | stack    | attributes or dates |
 *
 * ### Bucket axioms
 *
 * - |Measures| ≥ 1
 * - |ViewBy| ≤ 2
 * - |StackBy| ≤ 1
 * - |StackBy| = 1 ⇒ |Measures| ≤ 1
 * - |StackBy| = 0 ⇒ |Measures| ≤ 20
 * - |Measures| ≥ 2 ⇒ |StackBy| = 0
 * - ∀ a, b ∈ ViewBy (isDate(a) ∧ isDate(b) ⇒ dateDataset(a) = dateDataset(b))
 *
 * ## Dimensions
 *
 * The PluggableBarChart always creates two dimensional execution.
 *
 * - |StackBy| != 0 ⇒ [[StackBy[0]], [...ViewBy, MeasureGroupIdentifier]]
 * - |StackBy| = 0 ⇒ [[MeasureGroupIdentifier], [...ViewBy]]
 *
 * ## Sorts
 *
 * Unless the user specifies otherwise, the sorts used by default are:
 *
 * - |ViewBy| = 2 ∧ |Measures| ≥ 2 ∧ ¬stackMeasures ⇒ [attributeAreaSort(ViewBy[0]), measureSort(Measures[0])]
 * - |ViewBy| = 2 ∧ ¬(|Measures| ≥ 2 ∧ ¬stackMeasures) ⇒ [attributeAreaSort(ViewBy[0]), attributeAreaSort(ViewBy[1])]
 * - |ViewBy| = 1 ∧ (|StackBy| = 1 ∨ stackMeasures) ⇒ [attributeAreaSort(ViewBy[0])]
 * - |ViewBy| = 1 ∧ ¬(|StackBy| = 1 ∨ stackMeasures) ∧ |Measures| ≥ 1 ⇒ [measureSort(Measures[0])]
 *
 * In any other case the sorts are not used.
 *
 * If "enableChartsSorting" is enabled, the sorts can be changed by the user.
 */
var PluggableBarChart = /** @class */ (function (_super) {
    tslib_1.__extends(PluggableBarChart, _super);
    function PluggableBarChart(props) {
        var _this = _super.call(this, props) || this;
        _this.secondaryAxis = axis_1.AXIS_NAME.SECONDARY_X;
        _this.type = sdk_ui_1.VisualizationTypes.BAR;
        _this.defaultControlsProperties = {
            stackMeasures: false,
        };
        _this.initializeProperties(props.visualizationProperties);
        return _this;
    }
    PluggableBarChart.prototype.getSupportedPropertiesList = function () {
        return supportedProperties_1.BAR_CHART_SUPPORTED_PROPERTIES[this.axis || axis_1.AXIS.DUAL] || [];
    };
    PluggableBarChart.prototype.renderConfigurationPanel = function (insight) {
        var configPanelElement = this.getConfigPanelElement();
        if (configPanelElement) {
            this.renderFun(react_1.default.createElement(BarChartConfigurationPanel_1.default, { locale: this.locale, colors: this.colors, references: this.references, properties: this.visualizationProperties, propertiesMeta: this.propertiesMeta, insight: insight, pushData: this.handlePushData, type: this.type, isError: this.getIsError(), isLoading: this.isLoading, featureFlags: this.featureFlags, axis: this.axis }), configPanelElement);
        }
    };
    PluggableBarChart.prototype.getDefaultAndAvailableSort = function (referencePoint, canSortStackTotalValue) {
        if (this.isSortDisabled(referencePoint).disabled) {
            return {
                defaultSort: [],
                availableSorts: [],
            };
        }
        var buckets = referencePoint.buckets;
        var measures = bucketHelper_1.getBucketItems(buckets, sdk_ui_1.BucketNames.MEASURES);
        var viewBy = bucketHelper_1.getBucketItems(buckets, sdk_ui_1.BucketNames.VIEW);
        var stackBy = bucketHelper_1.getBucketItems(buckets, sdk_ui_1.BucketNames.STACK);
        var isStacked = !isEmpty_1.default(stackBy) || canSortStackTotalValue;
        if (viewBy.length === 2 && !isEmpty_1.default(measures)) {
            if (measures.length >= 2 && !canSortStackTotalValue) {
                return {
                    defaultSort: [
                        sdk_model_1.newAttributeAreaSort(viewBy[0].localIdentifier, "desc"),
                        sdk_model_1.newMeasureSort(measures[0].localIdentifier, "desc"),
                    ],
                    availableSorts: [
                        SortConfig_1.newAvailableSortsGroup(viewBy[0].localIdentifier),
                        SortConfig_1.newAvailableSortsGroup(viewBy[1].localIdentifier, measures.map(function (m) { return m.localIdentifier; })),
                    ],
                };
            }
            return {
                defaultSort: [
                    sdk_model_1.newAttributeAreaSort(viewBy[0].localIdentifier, "desc"),
                    isStacked
                        ? sdk_model_1.newAttributeAreaSort(viewBy[1].localIdentifier, "desc")
                        : sdk_model_1.newMeasureSort(measures[0].localIdentifier, "desc"),
                ],
                availableSorts: [
                    SortConfig_1.newAvailableSortsGroup(viewBy[0].localIdentifier),
                    SortConfig_1.newAvailableSortsGroup(viewBy[1].localIdentifier, isEmpty_1.default(stackBy) ? measures.map(function (m) { return m.localIdentifier; }) : [], true, isStacked || measures.length > 1),
                ],
            };
        }
        if (!isEmpty_1.default(viewBy) && isStacked) {
            return {
                defaultSort: [sdk_model_1.newAttributeAreaSort(viewBy[0].localIdentifier, "desc")],
                availableSorts: [
                    SortConfig_1.newAvailableSortsGroup(viewBy[0].localIdentifier, isEmpty_1.default(stackBy) ? measures.map(function (m) { return m.localIdentifier; }) : []),
                ],
            };
        }
        if (!isEmpty_1.default(viewBy) && !isEmpty_1.default(measures)) {
            return {
                defaultSort: [sdk_model_1.newMeasureSort(measures[0].localIdentifier, "desc")],
                availableSorts: [
                    SortConfig_1.newAvailableSortsGroup(viewBy[0].localIdentifier, measures.map(function (m) { return m.localIdentifier; }), true, measures.length > 1),
                ],
            };
        }
        return {
            defaultSort: [],
            availableSorts: [],
        };
    };
    PluggableBarChart.prototype.isSortDisabled = function (referencePoint) {
        var buckets = referencePoint.buckets;
        var viewBy = bucketHelper_1.getBucketItems(buckets, sdk_ui_1.BucketNames.VIEW);
        var measures = bucketHelper_1.getBucketItems(buckets, sdk_ui_1.BucketNames.MEASURES);
        var disabled = viewBy.length < 1 || measures.length < 1;
        var disabledExplanation = sort_1.getCustomSortDisabledExplanation(measures, viewBy, this.intl);
        return {
            disabled: disabled,
            disabledExplanation: disabledExplanation,
        };
    };
    PluggableBarChart.prototype.getSortConfig = function (referencePoint) {
        var buckets = referencePoint.buckets, properties = referencePoint.properties, previousAvailableSorts = referencePoint.availableSorts;
        var _a = this.getDefaultAndAvailableSort(referencePoint, sortHelpers_1.canSortStackTotalValue(buckets, properties)), defaultSort = _a.defaultSort, availableSorts = _a.availableSorts;
        var _b = this.isSortDisabled(referencePoint), disabled = _b.disabled, disabledExplanation = _b.disabledExplanation;
        return Promise.resolve(tslib_1.__assign({ supported: true, disabled: disabled, appliedSort: _super.prototype.reuseCurrentSort.call(this, previousAvailableSorts, properties, availableSorts, defaultSort), defaultSort: defaultSort, availableSorts: availableSorts }, (disabledExplanation && { disabledExplanation: disabledExplanation })));
    };
    return PluggableBarChart;
}(PluggableColumnBarCharts_1.PluggableColumnBarCharts));
exports.PluggableBarChart = PluggableBarChart;
//# sourceMappingURL=PluggableBarChart.js.map